import { useState, useEffect, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import useAuthStore from '../store/authStore';
import api from '../api/offlineClient';
import dataCache from '../services/dataCache';

const ROLES_ADMIN = ['admin', 'jefe', 'administrativo'];
const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
               'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

export default function AdminDocs() {
  const user = useAuthStore(s => s.user);
  if (!ROLES_ADMIN.includes(user?.rol)) {
    return <div style={{ padding: 32, color: 'var(--text-dim)', textAlign: 'center' }}>Acceso restringido.</div>;
  }
  return <AdminDocsInner user={user} />;
}

function AdminDocsInner({ user }) {
  const [tab, setTab] = useState('nominas');

  return (
    <div style={{ padding: 'clamp(12px,3vw,24px)', maxWidth: 920, margin: '0 auto' }}>
      <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)', marginBottom: 18 }}>
        Subir Documentos
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: 'var(--surface2)',
          border: '1px solid var(--border)', borderRadius: 9, padding: 4, width: 'fit-content' }}>
        {[['nominas','📜 Nóminas'],['instalaciones','📍 Instalaciones']].map(([v,l]) => (
          <button key={v} onClick={() => setTab(v)}
            style={{ background: tab === v ? 'var(--surface)' : 'transparent',
              border: 'none', color: tab === v ? 'var(--amber)' : 'var(--text-dim)',
              padding: '7px 18px', borderRadius: 6, cursor: 'pointer',
              fontWeight: 700, fontSize: 13, transition: 'all .15s' }}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'nominas'       && <TabNominas user={user} />}
      {tab === 'instalaciones' && <TabInstalaciones />}
    </div>
  );
}

/* ─── Tab Nóminas ─────────────────────────────────────────────────── */
function TabNominas({ user }) {
  const [employees, setEmployees] = useState([]);
  const [nominas,   setNominas]   = useState([]);
  const [loading,   setLoading]   = useState(true);

  const thisYear = new Date().getFullYear();
  const [selEmp,    setSelEmp]    = useState('');
  const [selMes,    setSelMes]    = useState(new Date().getMonth() + 1);
  const [selAño,    setSelAño]    = useState(thisYear);
  const [pdfFile,   setPdfFile]   = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, nomRes] = await Promise.all([
        api.get('/api/employees/list').catch(() => ({ data: [] })),
        api.get('/api/nominas/all').catch(() => ({ data: [] })),
      ]);
      setEmployees(empRes.data || []);
      setNominas(nomRes.data || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setPdfFile({ b64: ev.target.result.split(',')[1], name: file.name });
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  async function upload() {
    if (!selEmp || !pdfFile) { alert('Selecciona empleado y PDF'); return; }
    setUploading(true);
    try {
      const r = await api.post('/api/nominas/create', {
        employee_id: parseInt(selEmp), mes: selMes, año: selAño,
        file_b64: pdfFile.b64, file_name: pdfFile.name,
      });
      const emp = employees.find(e => String(e.id) === String(selEmp));
      setNominas(prev => {
        const filtered = prev.filter(n =>
          !(n.user_id === r.data.user_id && n.mes === r.data.mes && n.año === r.data.año));
        return [{ ...r.data, user_nombre: emp?.nombre || '' }, ...filtered]
          .sort((a, b) => b.año - a.año || b.mes - a.mes || (a.user_nombre||'').localeCompare(b.user_nombre||''));
      });
      setPdfFile(null);
    } catch (e) { alert('Error: ' + e.message); }
    finally { setUploading(false); }
  }

  async function deleteNomina(id) {
    if (!window.confirm('¿Eliminar esta nómina?')) return;
    try {
      await api.delete(`/api/nominas/${id}`);
      setNominas(prev => prev.filter(n => n.id !== id));
    } catch (e) { alert(e.message); }
  }

  async function downloadNomina(n) {
    try {
      const blob = await api.get(`/api/nominas/${n.id}/file`, { responseType: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = n.file_name || `nomina-${MESES[(n.mes||1)-1]}-${n.año}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch { alert('No se pudo descargar'); }
  }

  if (loading) return <Spinner />;

  const linkedEmps = employees.filter(e => !e.estado || e.estado !== 'baja');
  const years = [thisYear - 1, thisYear, thisYear + 1];

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {/* Upload */}
      <div style={CARD}>
        <div style={STITLE}>📤 Subir nómina</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 10, marginTop: 12 }}>
          <Fld label="Empleado">
            <select value={selEmp} onChange={e => setSelEmp(e.target.value)} style={SEL}>
              <option value="">Seleccionar…</option>
              {linkedEmps.map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}
            </select>
          </Fld>
          <Fld label="Mes">
            <select value={selMes} onChange={e => setSelMes(parseInt(e.target.value))} style={SEL}>
              {MESES.map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
            </select>
          </Fld>
          <Fld label="Año">
            <select value={selAño} onChange={e => setSelAño(parseInt(e.target.value))} style={SEL}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </Fld>
        </div>
        <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => fileRef.current?.click()}
            style={{ background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--amber)',
              padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
            {pdfFile ? '✓ ' + pdfFile.name.slice(0,32) : '📎 Seleccionar PDF'}
          </button>
          {pdfFile && (
            <button onClick={() => setPdfFile(null)}
              style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 18 }}>✕</button>
          )}
          <input ref={fileRef} type="file" accept="application/pdf,.pdf" style={{ display:'none' }} onChange={handleFile} />
          <button onClick={upload} disabled={!selEmp || !pdfFile || uploading}
            style={{ background: (!selEmp||!pdfFile) ? '#555' : 'var(--amber)', color: '#000',
              border: 'none', padding: '8px 18px', borderRadius: 7, fontWeight: 800, fontSize: 12,
              cursor: (!selEmp||!pdfFile||uploading) ? 'not-allowed' : 'pointer',
              opacity: (!selEmp||!pdfFile) ? 0.5 : 1 }}>
            {uploading ? 'Subiendo…' : '⬆ Subir'}
          </button>
        </div>
      </div>

      {/* Lista */}
      <div style={CARD}>
        <div style={STITLE}>📋 Nóminas subidas ({nominas.length})</div>
        {nominas.length === 0
          ? <div style={{ color: 'var(--text-dim)', fontSize: 12, marginTop: 10 }}>Sin nóminas aún.</div>
          : (
            <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
              {nominas.map(n => (
                <div key={n.id} style={ROW}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{n.user_nombre}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>
                      {MESES[(n.mes||1)-1]} {n.año} ·{' '}
                      {n.leida
                        ? <span style={{ color: '#4ade80' }}>Descargada ✓</span>
                        : <span style={{ color: '#fbbf24' }}>Pendiente</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => downloadNomina(n)} style={BTN_DL}>⬇ PDF</button>
                    <button onClick={() => deleteNomina(n.id)} style={BTN_DEL}>🗑</button>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}

/* ─── Tab Instalaciones ───────────────────────────────────────────── */
function parseSheetRows(wb, sheetName) {
  const ws = wb.Sheets[sheetName];
  if (!ws) return [];
  const rawRows = XLSX.utils.sheet_to_json(ws, { defval: null, raw: true });
  return rawRows.map(row => {
    const out = {};
    for (const [k, v] of Object.entries(row)) {
      if (v instanceof Date) {
        const y = v.getFullYear();
        const m = String(v.getMonth() + 1).padStart(2, '0');
        const d = String(v.getDate()).padStart(2, '0');
        out[k] = `${y}-${m}-${d}`;
      } else if (typeof v === 'number') {
        out[k] = Number.isInteger(v) ? String(v) : String(Math.round(v));
      } else {
        out[k] = v;
      }
    }
    return out;
  });
}

function TabInstalaciones() {
  const [xlsFile,    setXlsFile]    = useState(null);
  const [importing,  setImporting]  = useState(false);
  const [progress,   setProgress]   = useState(null); // null = oculto, 0-100 = visible
  const [progLabel,  setProgLabel]  = useState('');
  const [result,     setResult]     = useState(null);
  const [lastImport, setLastImport] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [selSheet,   setSelSheet]   = useState('');
  const fileRef = useRef();
  const wbRef   = useRef(null);

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setXlsFile(null);
    setResult(null);
    setSheetNames([]);
    setSelSheet('');
    setProgress(0);
    setProgLabel('Leyendo archivo…');
    e.target.value = '';

    const reader = new FileReader();
    reader.onprogress = ev => {
      if (ev.lengthComputable)
        setProgress(Math.round((ev.loaded / ev.total) * 40)); // 0–40%
    };
    reader.onload = ev => {
      setProgress(50);
      setProgLabel('Procesando Excel…');
      // setTimeout deja que React renderice el 50% antes de que XLSX bloquee el hilo
      setTimeout(() => {
        const wb = XLSX.read(ev.target.result, { type: 'array', cellDates: true });
        wbRef.current = wb;

        // Filtrar pestañas de instalaciones (ignora las de partes de trabajo S27 G9 etc.)
        const instSheets = wb.SheetNames.filter(n => /instalaci/i.test(n));
        const sheets = instSheets.length > 0 ? instSheets : [wb.SheetNames[0]];

        // Auto-seleccionar la del año en curso, si no la última
        const currentYear = String(new Date().getFullYear());
        const defaultSheet = sheets.find(n => n.includes(currentYear))
                           || sheets[sheets.length - 1];

        setSheetNames(sheets);
        setSelSheet(defaultSheet);

        // raw:true → numbers arrive as JS numbers (never "260,491"), dates as Date objects (via cellDates)
        const rows = parseSheetRows(wb, defaultSheet);
        setXlsFile({ rows, name: file.name, sheet: defaultSheet });
        setProgress(null);
        setProgLabel('');
      }, 30);
    };
    reader.readAsArrayBuffer(file);
  }

  function handleSheetChange(sheetName) {
    setSelSheet(sheetName);
    setResult(null);
    if (wbRef.current) {
      const rows = parseSheetRows(wbRef.current, sheetName);
      setXlsFile(prev => ({ ...prev, rows, sheet: sheetName }));
    }
  }

  async function importar() {
    if (!xlsFile) return;
    setImporting(true);
    setProgress(0);
    setProgLabel('Importando…');
    setResult(null);
    try {
      const CHUNK = 300;
      const { rows, name } = xlsFile;
      const totalChunks = Math.ceil(rows.length / CHUNK);
      let total = 0, inserted = 0, updated = 0, skipped = 0, duped = 0, detectedCols = [], skippedSamples = [];
      for (let ci = 0; ci < rows.length; ci += CHUNK) {
        const r = await api.post('/api/installations/import', { rows: rows.slice(ci, ci + CHUNK), offset: ci });
        total    += r.total    || 0;
        inserted += r.inserted || 0;
        updated  += r.updated  || 0;
        skipped  += r.skipped  || 0;
        duped    += r.duped    || 0;
        if (ci === 0 && r.detectedCols) detectedCols = r.detectedCols;
        if (r.skippedSamples?.length && skippedSamples.length < 3) skippedSamples.push(...r.skippedSamples);
        setProgress(Math.round(((Math.floor(ci / CHUNK) + 1) / totalChunks) * 100));
      }
      setResult({ total, inserted, updated, skipped, duped, detectedCols, skippedSamples });
      setLastImport({ date: new Date().toLocaleString('es-ES'), file: name });
      setXlsFile(null);
      if (inserted + updated > 0) {
        dataCache.invalidate();
        dataCache.preFetch(true).catch(() => {});
      }
    } catch (e) { alert('Error al importar: ' + e.message); }
    finally { setImporting(false); setProgress(null); setProgLabel(''); }
  }

  const busy = importing || progress !== null;

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={CARD}>
        <div style={STITLE}>📥 Importar archivo de instalaciones</div>
        <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 8, lineHeight: 1.6 }}>
          Sube el Excel semanal de instalaciones. Las filas se actualizarán si ya existen
          (por número de instalación), o se crearán si son nuevas.
          Las columnas se detectan automáticamente por el nombre del encabezado.
        </div>

        <div style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => !busy && fileRef.current?.click()}
            style={{ background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--amber)',
              padding: '9px 16px', borderRadius: 6, cursor: busy ? 'not-allowed' : 'pointer',
              fontSize: 13, fontWeight: 700, opacity: busy ? 0.5 : 1 }}>
            {xlsFile ? '✓ ' + xlsFile.name.slice(0,36) : '📂 Seleccionar Excel (.xlsx)'}
          </button>
          {xlsFile && !busy && (
            <button onClick={() => { setXlsFile(null); setSheetNames([]); setSelSheet(''); wbRef.current = null; }}
              style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 18 }}>✕</button>
          )}
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }} onChange={handleFile} />

          {/* Selector de pestaña — solo si hay varias pestañas de instalaciones */}
          {sheetNames.length > 1 && xlsFile && !busy && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, color: 'var(--text-dim)', fontWeight: 700, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Pestaña:</span>
              <select
                value={selSheet}
                onChange={e => handleSheetChange(e.target.value)}
                style={{ ...SEL, width: 'auto', minWidth: 160, padding: '7px 10px', fontSize: 12 }}
              >
                {sheetNames.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          )}

          <button onClick={importar} disabled={!xlsFile || busy}
            style={{ background: xlsFile && !busy ? 'rgba(74,222,128,.15)' : 'var(--surface2)',
              color: xlsFile && !busy ? '#4ade80' : 'var(--text-dim)',
              border: `1px solid ${xlsFile && !busy ? 'rgba(74,222,128,.4)' : 'var(--border)'}`,
              padding: '9px 20px', borderRadius: 7, fontWeight: 800, fontSize: 13,
              cursor: (!xlsFile || busy) ? 'not-allowed' : 'pointer',
              opacity: !xlsFile ? 0.5 : 1 }}>
            {importing ? '⏳ Importando…' : '🔄 Importar'}
          </button>

          {progress !== null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 160 }}>
              <div style={{ flex: 1, height: 8, background: 'var(--surface-deep)', borderRadius: 99,
                  border: '1px solid var(--border)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${progress}%`, background: '#4ade80',
                    borderRadius: 99, transition: 'width .3s ease' }} />
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>
                {progLabel} {progress}%
              </span>
            </div>
          )}
        </div>

        {/* Resultado */}
        {result && (
          <div style={{ marginTop: 16, background: (result.inserted + result.updated) === 0 ? 'rgba(248,113,113,.07)' : 'rgba(74,222,128,.07)',
              border: `1px solid ${(result.inserted + result.updated) === 0 ? 'rgba(248,113,113,.3)' : 'rgba(74,222,128,.3)'}`,
              borderRadius: 8, padding: '12px 16px' }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: (result.inserted + result.updated) === 0 ? '#f87171' : '#4ade80', marginBottom: 8 }}>
              {(result.inserted + result.updated) === 0 ? '⚠ No se importó ninguna fila' : '✓ Importación completada'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(120px,1fr))', gap: 8 }}>
              {[
                ['Total filas',  result.total,                            false],
                ['Nuevas',       result.inserted,                         false],
                ['Actualizadas', result.updated,                          false],
                ['Sin nº/dir.',  result.skipped - (result.duped || 0),   true],
                ...(result.duped > 0 ? [['Duplicadas', result.duped, false]] : []),
              ].map(([label, val, isError]) => (
                <div key={label} style={{ background: 'var(--surface-deep)', borderRadius: 6, padding: '8px 10px' }}>
                  <div style={{ fontSize: 18, fontWeight: 900, color: isError && val > 0 ? '#f87171' : '#4ade80' }}>{val}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>{label}</div>
                </div>
              ))}
            </div>
            {result.detectedCols && result.detectedCols.length > 0 && (
              <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-dim)' }}>
                Columnas detectadas: <span style={{ color: 'var(--text)' }}>{result.detectedCols.join(' · ')}</span>
              </div>
            )}
            {(result.inserted + result.updated) === 0 && result.detectedCols && result.detectedCols.length === 0 && (
              <div style={{ marginTop: 10, fontSize: 11, color: '#fbbf24' }}>
                No se reconoció ninguna columna. Asegúrate de que la primera fila del Excel contiene los encabezados
                (ej: «N° Instalación», «Dirección», «Cliente»…).
              </div>
            )}
            {(result.inserted + result.updated) === 0 && result.detectedCols && result.detectedCols.length > 0 && !result.detectedCols.includes('numero_instalacion') && (
              <div style={{ marginTop: 10, fontSize: 11, color: '#fbbf24' }}>
                La columna «Número de instalación» no fue detectada — es obligatoria. Revisa el nombre del encabezado en el Excel.
              </div>
            )}
            {result.skippedSamples?.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 11, color: '#fbbf24', marginBottom: 4 }}>
                  ⚠ Filas sin número de instalación (no importadas). Revisa estas filas en tu Excel:
                </div>
                {result.skippedSamples.slice(0, 3).map((s, i) => (
                  <div key={i} style={{ fontSize: 10, color: 'var(--text-dim)', background: 'var(--surface-deep)',
                    borderRadius: 5, padding: '5px 8px', marginBottom: 4, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {Object.entries(s).map(([k, v]) => `${k}: ${v ?? '(vacío)'}`).join(' | ')}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {lastImport && !result && (
          <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-dim)' }}>
            Última importación: {lastImport.date} · {lastImport.file}
          </div>
        )}
      </div>

      <div style={{ ...CARD, borderColor: 'var(--border)' }}>
        <div style={{ fontSize: 11, color: 'var(--amber)', fontWeight: 700, marginBottom: 8 }}>
          ℹ Columnas reconocidas automáticamente
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.7 }}>
          <b style={{ color: 'var(--text)' }}>Número de instalación</b> (clave única) ·{' '}
          Referencia interna · Cliente · Dirección · Población · Provincia ·{' '}
          Familia · Descripción · Estado · Responsable · Observaciones ·{' '}
          Fecha entrada · Medio entrada
          <br />
          Los nombres de columna se detectan de forma flexible (mayúsculas, tildes y espacios son ignorados).
        </div>
      </div>
    </div>
  );
}

/* ─── Helpers ─────────────────────────────────────────────────────── */
function Spinner() {
  return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-dim)' }}>Cargando…</div>;
}
function Fld({ label, children }) {
  return (
    <div>
      <label style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, letterSpacing: '.05em',
        textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

const CARD   = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px' };
const STITLE = { fontSize: 10, color: 'var(--amber)', fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase' };
const SEL    = { background: 'var(--surface-deep)', border: '1.5px solid var(--border)', borderRadius: 7,
  color: 'var(--text)', padding: '8px 10px', fontSize: 12, fontFamily: 'inherit', width: '100%' };
const ROW    = { background: 'var(--surface-deep)', border: '1px solid var(--border)', borderRadius: 7,
  padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 12 };
const BTN_DL  = { background: 'rgba(74,222,128,.1)', border: '1px solid rgba(74,222,128,.3)', color: '#4ade80',
  borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 700 };
const BTN_DEL = { background: 'rgba(248,113,113,.1)', border: '1px solid rgba(248,113,113,.3)', color: '#f87171',
  borderRadius: 6, padding: '5px 8px', cursor: 'pointer', fontSize: 13 };
